export type ScrollArrowsOptions = {
	root: HTMLElement
	scrollContainer: HTMLElement
	track?: HTMLElement
	prevButton?: HTMLElement
	nextButton?: HTMLElement
}

const OVERFLOW_CLASS = 'has-overflow'
const START_CLASS = 'is-at-start'
const END_CLASS = 'is-at-end'
const EDGE_THRESHOLD = 2

export class ScrollArrows {
	root: HTMLElement
	scrollContainer: HTMLElement
	track: HTMLElement
	prevButton?: HTMLElement
	nextButton?: HTMLElement
	resizeObserver: ResizeObserver
	rafId?: number
	boundOnScroll: () => void
	boundOnPrev: () => void
	boundOnNext: () => void

	constructor(options: ScrollArrowsOptions) {
		this.root = options.root
		this.scrollContainer = options.scrollContainer
		this.track = options.track ?? options.scrollContainer
		this.prevButton = options.prevButton
		this.nextButton = options.nextButton

		this.boundOnScroll = this.onScroll.bind(this)
		this.boundOnPrev = () => this.step(-1)
		this.boundOnNext = () => this.step(1)

		this.prevButton?.addEventListener('click', this.boundOnPrev)
		this.nextButton?.addEventListener('click', this.boundOnNext)
		this.scrollContainer.addEventListener('scroll', this.boundOnScroll, { passive: true })

		this.resizeObserver = new ResizeObserver(() => this.updateOverflowState())
		this.resizeObserver.observe(this.scrollContainer)
		this.resizeObserver.observe(this.track)

		this.updateOverflowState()
	}

	onScroll() {
		if (this.rafId) return
		this.rafId = requestAnimationFrame(() => {
			this.updateEdgeState()
			this.rafId = undefined
		})
	}

	getStepAmount(): number {
		const firstChild = this.track.firstElementChild as HTMLElement | null
		if (!firstChild) return this.scrollContainer.clientWidth
		const trackStyle = window.getComputedStyle(this.track)
		const gap = parseFloat(trackStyle.columnGap || trackStyle.gap || '0') || 0
		return firstChild.getBoundingClientRect().width + gap
	}

	step(direction: 1 | -1) {
		this.scrollContainer.scrollBy({ left: direction * this.getStepAmount(), behavior: 'smooth' })
	}

	updateOverflowState() {
		const hasOverflow = this.scrollContainer.scrollWidth > this.scrollContainer.clientWidth + EDGE_THRESHOLD
		this.root.classList.toggle(OVERFLOW_CLASS, hasOverflow)
		this.updateEdgeState()
	}

	updateEdgeState() {
		const { scrollLeft, scrollWidth, clientWidth } = this.scrollContainer
		const isAtStart = scrollLeft <= EDGE_THRESHOLD
		const isAtEnd = scrollLeft >= scrollWidth - clientWidth - EDGE_THRESHOLD
		this.root.classList.toggle(START_CLASS, isAtStart)
		this.root.classList.toggle(END_CLASS, isAtEnd)
	}

	destroy() {
		this.prevButton?.removeEventListener('click', this.boundOnPrev)
		this.nextButton?.removeEventListener('click', this.boundOnNext)
		this.scrollContainer.removeEventListener('scroll', this.boundOnScroll)
		this.resizeObserver.disconnect()
		if (this.rafId) cancelAnimationFrame(this.rafId)
	}
}
